import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Star, ChevronRight, Grid3x3, GitBranch, Plus, X, Send, Compass, Pencil, Trash2, Trash, RotateCcw, ChevronDown, ChevronUp, Upload, UserPlus, GripVertical, UploadCloud, FileText, AlertTriangle, GraduationCap, Layers, LayoutGrid, Signal, TrendingUp, Zap, PlayCircle, Newspaper, FileText as FileTextIcon, BookOpen, Headphones, HelpCircle, Book, Sparkles, RefreshCw, ExternalLink } from "lucide-react";
import Slideshow from "./concept2/Slideshow";
import Concept2Modal from "./concept2/Concept2Modal";
import Concept2Viewer from "./concept2/Concept2Viewer";
import Concept2MindMap from "./concept2/Concept2MindMap";
import Concept2CreateCollectionDialog from "./concept2/Concept2CreateCollectionDialog";
import Concept2BulkImportModal from "./concept2/Concept2BulkImportModal";
import Concept2AssignDialog from "./concept2/Concept2AssignDialog";
import "./LearningHubConcept2.css";

// ── Filter dropdown values ──
const LEVELS = [
    { key: "all",          label: "All levels",  icon: LayoutGrid,  tint: "#1B2333" },
    { key: "beginner",     label: "Beginner",    icon: Signal,      tint: "#059669" },
    { key: "intermediate", label: "Intermediate", icon: TrendingUp, tint: "#EA580C" },
    { key: "advanced",     label: "Advanced",    icon: Zap,         tint: "#DC2626" },
];
const TYPES = [
    { key: "all",     label: "All types", icon: LayoutGrid,   tint: "#1B2333" },
    { key: "video",   label: "Video",     icon: PlayCircle,   tint: "#DC2626" },
    { key: "article", label: "Article",   icon: Newspaper,    tint: "#0EA5E9" },
    { key: "doc",     label: "Doc",       icon: FileTextIcon, tint: "#8B5CF6" },
    { key: "course",  label: "Course",    icon: BookOpen,     tint: "#2EB4AC" },
    { key: "podcast", label: "Podcast",   icon: Headphones,   tint: "#EC4899" },
    { key: "quiz",    label: "Quiz",      icon: HelpCircle,   tint: "#F59E0B" },
    { key: "book",    label: "Book",      icon: Book,         tint: "#6D5DF3" },
];

const WELCOME_ICONS_BG = ["var(--teal)", "#7C6FE0", "#E7A33E", "#2EB4AC", "#E45B6B", "var(--navy)"];
const WELCOME_ROWS = [
    { title: "Team playbooks front and centre", body: "Your department's most-loved collections sit at the top so onboarding + refreshers stay one scroll away." },
    { title: "Cross-team browsing",              body: "Every other department's library is grouped below — spot growth patterns, pinch what works." },
    { title: "Filter by level and type",         body: "The left rail slices the catalog by Beginner / Intermediate / Advanced and by media type." },
    { title: "Nested folders",                    body: "Inside any collection, organise resources into unlimited folders → subfolders. Drag to reorder." },
    { title: "Hub Assistant",                     body: "The teal circle bottom-right can answer questions like \"what's new in Amazon?\"." },
    { title: "Mind Map view",                     body: "Prefer a knowledge-graph? Flip Grid → Mind map above and see how every collection connects." },
];

const WelcomeModal = ({ userName, onClose }) => createPortal(
    <div className="lh-c2-welcome-backdrop lh-c2" onClick={onClose} data-testid="lh-c2-welcome">
        <div className="lh-c2-welcome" onClick={(e) => e.stopPropagation()}>
            <div className="lh-c2-welcome-head">
                <h2>Welcome to Odyssey Learning Hub, {userName?.split(" ")[0] || "there"}!</h2>
                <p>Everything you need to grow with confidence — all in one place.</p>
            </div>
            <div className="lh-c2-welcome-body">
                {WELCOME_ROWS.map((r, i) => (
                    <div key={i} className="lh-c2-welcome-row">
                        <div className="lh-c2-welcome-ic" style={{ background: WELCOME_ICONS_BG[i] }}>{i + 1}</div>
                        <div className="lh-c2-welcome-row-text">
                            <h4>{r.title}</h4>
                            <p>{r.body}</p>
                        </div>
                    </div>
                ))}
            </div>
            <div className="lh-c2-welcome-footer">
                <button className="lh-c2-btn lh-c2-btn-solid" onClick={onClose} data-testid="lh-c2-welcome-close">Explore the Hub</button>
            </div>
        </div>
    </div>,
    document.body,
);

/* Native HTML5 drag-and-drop hook shared by every dept-shelf. Each
 * `dragProps` object it hands back wires a card into the same drag
 * session: onDragStart tags the source index; onDragOver highlights
 * the current drop slot; onDrop calls the parent's `onReorder(from, to)`
 * to persist the new sequence server-side.
 *
 * Also exposes `moveUp(idx)` / `moveDown(idx)` so callers can render
 * explicit ▲/▼ arrow buttons — a 100% reliable keyboard/touch-friendly
 * alternative to HTML5 drag (which is finicky on trackpads and mobile).
 * Position-aware drop: while dragging, the drop side (before/after) is
 * tracked from the pointer's X position inside the target so the drop
 * indicator matches what will happen when the user lets go. */
const useShelfDnD = (items, canManage, onReorder) => {
    const [dragIdx, setDragIdx] = useState(-1);
    const [overIdx, setOverIdx] = useState(-1);
    const [overSide, setOverSide] = useState(null); // "before" | "after"
    const commit = (from, to) => {
        if (from === -1 || to === -1 || from === to) return;
        if (!items[from] || !items[to]) return;
        onReorder?.(from, to);
    };
    return {
        moveUp: (idx) => {
            if (idx <= 0 || !items[idx] || !canManage(items[idx])) return;
            commit(idx, idx - 1);
        },
        moveDown: (idx) => {
            if (idx < 0 || idx >= items.length - 1 || !items[idx] || !canManage(items[idx])) return;
            commit(idx, idx + 1);
        },
        canMoveUp: (idx) => idx > 0 && !!items[idx] && canManage(items[idx]),
        canMoveDown: (idx) => idx >= 0 && idx < items.length - 1 && !!items[idx] && canManage(items[idx]),
        propsFor: (idx) => {
            const item = items[idx];
            if (!item || !onReorder || !canManage(item)) return {};
            const canUp = idx > 0;
            const canDown = idx < items.length - 1;
            return {
                dragging: dragIdx === idx,
                dropHint: overIdx === idx && dragIdx !== -1 && dragIdx !== idx,
                dropSide: overIdx === idx ? overSide : null,
                canMoveUp: canUp,
                canMoveDown: canDown,
                moveUp: () => { if (canUp) commit(idx, idx - 1); },
                moveDown: () => { if (canDown) commit(idx, idx + 1); },
                onDragStart: (e) => {
                    setDragIdx(idx);
                    try { e.dataTransfer.effectAllowed = "move"; } catch { /* Safari */ }
                    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* IE */ }
                },
                onDragOver: (e) => {
                    if (dragIdx === -1) return;
                    e.preventDefault();
                    // Compute drop side from pointer X within the target so
                    // the indicator line renders on the correct edge.
                    const rect = e.currentTarget.getBoundingClientRect();
                    const midX = rect.left + rect.width / 2;
                    const side = e.clientX < midX ? "before" : "after";
                    if (overIdx !== idx) setOverIdx(idx);
                    if (overSide !== side) setOverSide(side);
                },
                onDragLeave: () => { if (overIdx === idx) { setOverIdx(-1); setOverSide(null); } },
                onDrop: (e) => {
                    e.preventDefault();
                    const from = dragIdx;
                    const to = idx;
                    const side = overSide;
                    setDragIdx(-1); setOverIdx(-1); setOverSide(null);
                    if (from === -1 || to === -1 || from === to) return;
                    // Adjust target index for "before" vs "after" so the
                    // dropped card lands exactly where the indicator shows.
                    let target = to;
                    if (side === "after" && from > to) target = to + 1;
                    if (side === "before" && from < to) target = to - 1;
                    if (target < 0) target = 0;
                    if (target > items.length - 1) target = items.length - 1;
                    if (target !== from) onReorder(from, target);
                },
                onDragEnd: () => { setDragIdx(-1); setOverIdx(-1); setOverSide(null); },
            };
        },
    };
};

/* A small palette used when a collection has neither cover_image nor a
 * dept-driven `cover_color`. Keyed by the first char of the collection
 * name so cards stay stable across renders. */
const CARD_TINTS = ["#2EB4AC", "#E7A33E", "#5B7FDE", "#C86B85", "#7FA65C", "#C6543F", "#1B2333", "#38B26D"];
const tintForCollection = (c) => {
    if (c?.cover_color) return c.cover_color;
    const s = (c?.name || c?.id || "");
    const code = s.charCodeAt(0) || 0;
    return CARD_TINTS[code % CARD_TINTS.length];
};

/* LoremFlickr fallback — auto-picks a real photo based on the collection
 * name. Keyword strategy: drop stop-words + punctuation and comma-join
 * the first 3 tokens so LoremFlickr's tag search yields something
 * relevant (e.g. "Amazon PPC 101" → "amazon,ppc,101"). `lock=<seed>`
 * freezes the exact image for a given collection so successive renders
 * stay stable — same collection = same photo, no flicker on refresh. */
const STOP_WORDS = new Set(["the", "a", "an", "and", "or", "of", "for", "to", "in", "on", "with", "by"]);
const loremKeywords = (name) =>
    (name || "learning")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter((w) => w && !STOP_WORDS.has(w))
        .slice(0, 3)
        .join(",") || "learning";
const cheapHash = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
};
const autoCoverFor = (c) => {
    const kw = loremKeywords(c?.name);
    const seed = cheapHash(c?.id || c?.name || "x");
    return `https://loremflickr.com/640/360/${encodeURIComponent(kw)}?lock=${seed}`;
};
const picsumFallback = (c) => `https://picsum.photos/seed/${cheapHash(c?.id || c?.name || "x")}/640/360`;

export const CollCard = ({ c, pinned, onOpen, onEdit, onDelete, onAssign, onReroll, canManage, dragProps, autoCovers, showCover }) => {
    const folders = c.folder_count || 0;
    const resources = c.resource_count || 0;
    const tint = tintForCollection(c);
    // A cover renders only when the org has auto-covers enabled OR the
    // user just generated one this session (`showCover` override).
    const effectiveShow = showCover || autoCovers;
    const initialSrc = effectiveShow ? (c.cover_image || (autoCovers ? autoCoverFor(c) : null)) : null;
    const [coverSrc, setCoverSrc] = React.useState(initialSrc);
    React.useEffect(() => {
        setCoverSrc(effectiveShow ? (c.cover_image || (autoCovers ? autoCoverFor(c) : null)) : null);
    }, [c.cover_image, c.id, autoCovers, effectiveShow]);
    const onCoverErr = () => {
        // LoremFlickr 500s on keywords with zero matching Flickr photos;
        // Picsum always resolves.
        const fb = picsumFallback(c);
        if (coverSrc !== fb) setCoverSrc(fb);
    };
    const dnd = dragProps || {};
    const canDrag = !!dnd.onDragStart;
    const canReorder = !!(dnd.canMoveUp || dnd.canMoveDown);
    return (
        <div
            className={`lh-c2-coll-card-wrap ${dnd.dragging ? "is-dragging" : ""} ${dnd.dropHint ? "is-drop-target" : ""} ${dnd.dropSide ? `drop-${dnd.dropSide}` : ""}`}
            data-testid={`lh-c2-coll-wrap-${c.id}`}
            onDragOver={dnd.onDragOver}
            onDragLeave={dnd.onDragLeave}
            onDrop={dnd.onDrop}
        >
            <button className="lh-c2-coll-card" onClick={() => onOpen(c)} data-testid={`lh-c2-coll-${c.id}`}>
                <div className="lh-c2-coll-cover" aria-hidden>
                    {!coverSrc ? (
                        <div className="lh-c2-coll-cover-solid" style={{ background: `linear-gradient(150deg, ${tint}, ${tint}CC 60%, ${tint}99)` }}>
                            <div className="lh-c2-coll-cover-dots" />
                        </div>
                    ) : (
                        <>
                            <img
                                src={coverSrc}
                                alt=""
                                loading="lazy"
                                onError={onCoverErr}
                                className="lh-c2-coll-cover-img"
                            />
                            <div className="lh-c2-coll-cover-tint" style={{ background: `linear-gradient(150deg, ${tint}55, transparent 60%)` }} />
                        </>
                    )}
                </div>
                {pinned && <span className="lh-c2-ribbon">Popular</span>}
                <div className="lh-c2-coll-body">
                    <h4>{c.name}</h4>
                    <div className="lh-c2-coll-meta">{folders} folder{folders === 1 ? "" : "s"} · {resources} resource{resources === 1 ? "" : "s"}</div>
                    {c.owner_department_name && <span className="lh-c2-coll-tag" title={c.owner_department_alias && c.owner_department_alias !== c.owner_department_name ? c.owner_department_name : undefined}>{c.owner_department_alias || c.owner_department_name}</span>}
                </div>
            </button>
            {canReorder && (
                <div className="lh-c2-reorder-arrows" data-testid={`lh-c2-coll-arrows-${c.id}`}>
                    <button
                        type="button"
                        className="lh-c2-arrow-btn"
                        onClick={(e) => { e.stopPropagation(); dnd.moveUp?.(); }}
                        disabled={dnd.canMoveUp === false}
                        title="Move left"
                        aria-label="Move left"
                        data-testid={`lh-c2-coll-up-${c.id}`}
                    ><ChevronUp size={12} /></button>
                    <button
                        type="button"
                        className="lh-c2-arrow-btn"
                        onClick={(e) => { e.stopPropagation(); dnd.moveDown?.(); }}
                        disabled={dnd.canMoveDown === false}
                        title="Move right"
                        aria-label="Move right"
                        data-testid={`lh-c2-coll-down-${c.id}`}
                    ><ChevronDown size={12} /></button>
                </div>
            )}
            {canDrag && (
                <span
                    className="lh-c2-drag-handle"
                    title="Drag to reorder"
                    draggable
                    onDragStart={dnd.onDragStart}
                    onDragEnd={dnd.onDragEnd}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    data-testid={`lh-c2-coll-drag-${c.id}`}
                    aria-label="Drag to reorder"
                >
                    <GripVertical size={13} />
                </span>
            )}
            <div className="lh-c2-coll-actions">
                <button
                    className="lh-c2-coll-actionbtn"
                    onClick={(e) => { e.stopPropagation(); onReroll?.(c); }}
                    title={c.cover_image ? "Regenerate cover with Nano Banana" : "Generate cover with Nano Banana"}
                    data-testid={`lh-c2-coll-reroll-${c.id}`}
                ><Sparkles size={12} /></button>
                {canManage && (
                    <>
                        <button
                            className="lh-c2-coll-actionbtn"
                            onClick={(e) => { e.stopPropagation(); onAssign(c); }}
                            title="Assign to people"
                            data-testid={`lh-c2-coll-assign-${c.id}`}
                        ><UserPlus size={12} /></button>
                        <button
                            className="lh-c2-coll-actionbtn"
                            onClick={(e) => { e.stopPropagation(); onEdit(c); }}
                            title="Edit collection"
                            data-testid={`lh-c2-coll-edit-${c.id}`}
                        ><Pencil size={12} /></button>
                        <button
                            className="lh-c2-coll-actionbtn danger"
                            onClick={(e) => { e.stopPropagation(); onDelete(c); }}
                            title="Delete collection"
                            data-testid={`lh-c2-coll-del-${c.id}`}
                        ><Trash2 size={12} /></button>
                    </>
                )}
            </div>
        </div>
    );
};

const ViewAllButton = ({ deptId, count, expanded, onExpand }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    if (expanded) {
        return (
            <button
                className="lh-c2-btn-viewall expanded"
                onClick={onExpand}
                data-testid={`lh-c2-viewall-${deptId}`}
            >
                Show fewer collections <ChevronUp size={15} />
            </button>
        );
    }
    return (
        <div className="lh-c2-viewall-wrap" ref={wrapRef}>
            <button
                className="lh-c2-btn-viewall"
                onClick={() => setOpen((v) => !v)}
                data-testid={`lh-c2-viewall-${deptId}`}
                aria-expanded={open}
            >
                View all {count} collections <ChevronDown size={15} />
            </button>
            {open && (
                <div className="lh-c2-viewall-menu" role="menu" data-testid={`lh-c2-viewall-menu-${deptId}`}>
                    <button
                        onClick={() => { setOpen(false); onExpand(); }}
                        data-testid={`lh-c2-viewall-expand-${deptId}`}
                    >
                        <ChevronDown size={13} /> Expand here
                    </button>
                    <a
                        href={`/learning/department/${deptId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        data-testid={`lh-c2-viewall-page-${deptId}`}
                    >
                        <ExternalLink size={13} /> Open in new page
                    </a>
                </div>
            )}
        </div>
    );
};


export const DeptShelf = ({ deptId, deptName, collections, maxCards = 4, expanded, onToggle, onOpen, onEdit, onDelete, onAssign, onReroll, canManage, onReorder, autoCovers, freshCovers, hideViewAll = false }) => {
    const isExpanded = !!expanded;
    const shown = isExpanded ? collections : collections.slice(0, maxCards);
    const hasMore = collections.length > maxCards;
    // Per-shelf drag-state — the module-level `ref` avoids re-render churn
    // while the pointer moves. We commit the new sequence to the parent on
    // drop, and let the parent persist via `PUT /collections/reorder`.
    const dnd = useShelfDnD(shown, canManage, onReorder);
    if (!collections.length) return null;
    return (
        <div className="lh-c2-dept-row" data-testid={`lh-c2-dept-${deptId || "unassigned"}`}>
            <div className="lh-c2-dept-row-head">
                <div className="lh-c2-dept-row-head-left">
                    <h3>{deptName}</h3>
                    <span className="count">{collections.length} collection{collections.length === 1 ? "" : "s"}</span>
                </div>
            </div>
            <div className="lh-c2-shelf">
                {shown.map((c, i) => (
                    <CollCard
                        key={c.id}
                        c={c}
                        pinned={i === 0}
                        onOpen={onOpen}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onAssign={onAssign}
                        onReroll={onReroll}
                        canManage={canManage(c)}
                        dragProps={dnd.propsFor(i)}
                        autoCovers={autoCovers}
                        showCover={!!freshCovers?.has?.(c.id)}
                    />
                ))}
            </div>
            {hasMore && deptId && !hideViewAll && (
                <div className="lh-c2-viewall-row">
                    <ViewAllButton
                        deptId={deptId}
                        count={collections.length}
                        expanded={isExpanded}
                        onExpand={() => onToggle(deptId)}
                    />
                </div>
            )}
        </div>
    );
};

// Same shelf UX as DeptShelf but specialised for the "My Department"
// preview row on the home page — factored out so we can safely call
// the useShelfDnD hook (which would break Rules of Hooks if invoked
// inside a conditional IIFE in the parent).
const MyDeptShelf = ({ collections, canManageCollection, searchQuery, reorderCollectionsInShelf, setActiveCollection, setEditingCollection, deleteCollection, openAssignForCollection, rerollCover, autoCovers, freshCovers }) => {
    const dnd = useShelfDnD(
        collections,
        (c) => canManageCollection(c),
        searchQuery ? null : (from, to) => reorderCollectionsInShelf(collections, from, to),
    );
    return (
        <div className="lh-c2-shelf">
            {collections.map((c, i) => (
                <CollCard
                    key={c.id}
                    c={c}
                    pinned={i === 0}
                    onOpen={setActiveCollection}
                    onEdit={setEditingCollection}
                    onDelete={deleteCollection}
                    onAssign={openAssignForCollection}
                    onReroll={rerollCover}
                    canManage={canManageCollection(c)}
                    dragProps={dnd.propsFor(i)}
                    autoCovers={autoCovers}
                    showCover={freshCovers.has(c.id)}
                />
            ))}
        </div>
    );
};

// Sample queries the mockup rotates through in the hero search
// placeholder (typewriter effect — types + deletes each in turn).
const SAMPLE_QUERIES = [
    "AMS bidding strategy",
    "Blinkit dashboard walkthrough",
    "Sponsored Brands playbook",
    "Warehouse SOP: inbound to shelf",
    "On-page SEO checklist",
    "Meta creative testing",
];

// Kind → 2-letter code + colour, matches the resource pill palette used
// inside Concept2Modal so cards feel like the same design system.
const AI_KIND_META = {
    collection: { code: "CO", color: "#2EB4AC", label: "Collection" },
    folder:     { code: "FO", color: "#5B7FDE", label: "Folder" },
    resource:   { code: "RE", color: "#E7A33E", label: "Resource" },
};

// Floating AI Search results panel — renders under the grand search box
// when Odyssey's AI has ranked matches for the current query. Each row
// shows title + dept + one-line rationale; clicking opens the content.
const AiSearchPanel = ({ loading, results, error, cached, query, onOpen, onClose }) => {
    const empty = !loading && !error && (!results || results.length === 0);
    return (
        <div className="lh-c2-ai-panel" data-testid="lh-c2-ai-panel">
            <div className="lh-c2-ai-panel-head">
                <div className="lh-c2-ai-panel-title">
                    <Sparkles size={13} />
                    <span>Odyssey AI · smart matches for “{query.trim().slice(0, 60)}”</span>
                    {cached && <span className="lh-c2-ai-cached">from cache</span>}
                </div>
                <button
                    type="button"
                    className="lh-c2-ai-close"
                    onClick={onClose}
                    aria-label="Close AI search"
                    data-testid="lh-c2-ai-close"
                ><X size={13} /></button>
            </div>
            {loading && (
                <div className="lh-c2-ai-loading" data-testid="lh-c2-ai-loading">
                    <RotateCcw size={13} className="lh-c2-ai-spin" />
                    Reading the library, scoring matches…
                </div>
            )}
            {error && !loading && (
                <div className="lh-c2-ai-error" data-testid="lh-c2-ai-error">{error}</div>
            )}
            {empty && (
                <div className="lh-c2-ai-empty">No smart matches yet — refine the query or use the filters below.</div>
            )}
            {!loading && !error && results && results.length > 0 && (
                <div className="lh-c2-ai-list">
                    {results.map((r, i) => {
                        const meta = AI_KIND_META[r.kind] || AI_KIND_META.resource;
                        const bits = [];
                        if (r.dept) bits.push(r.dept);
                        if (r.kind === "resource") {
                            if (r.type) bits.push(r.type[0].toUpperCase() + r.type.slice(1));
                            if (r.level) bits.push(r.level[0].toUpperCase() + r.level.slice(1));
                            if (r.duration_minutes) bits.push(`${r.duration_minutes} min`);
                            if (r.collection_name) bits.push(`in ${r.collection_name}`);
                        }
                        if (r.kind === "folder" && r.collection_name) bits.push(`in ${r.collection_name}`);
                        return (
                            <button
                                key={`${r.kind}-${r.id}`}
                                type="button"
                                className="lh-c2-ai-row"
                                onClick={() => onOpen(r)}
                                data-testid={`lh-c2-ai-row-${r.id}`}
                            >
                                <span className="lh-c2-ai-rank">{i + 1}</span>
                                <span className="lh-c2-ai-pill" style={{ background: meta.color }}>{meta.code}</span>
                                <div className="lh-c2-ai-body">
                                    <div className="lh-c2-ai-title">{r.name}</div>
                                    <div className="lh-c2-ai-meta">{bits.join(" · ")}</div>
                                    {r.why && <div className="lh-c2-ai-why">↳ {r.why}</div>}
                                </div>
                                <ChevronRight size={14} className="lh-c2-ai-chev" />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/** Mockup-parity typewriter hook — types a sample query one char at a
 *  time (55 ms/char), pauses 1.3s, deletes (28 ms/char), pauses 350ms,
 *  cycles. Freezes while the input is focused OR has any user text. */
const useTypewriterPlaceholder = (isFocused, hasValue) => {
    const [text, setText] = useState("");
    const stateRef = useRef({ idx: 0, char: 0, deleting: false });
    useEffect(() => {
        if (isFocused || hasValue) { setText(""); return; }
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            const s = stateRef.current;
            const full = SAMPLE_QUERIES[s.idx];
            if (!s.deleting) {
                s.char += 1;
                setText(full.slice(0, s.char));
                if (s.char === full.length) {
                    s.deleting = true;
                    setTimeout(tick, 1300);
                    return;
                }
                setTimeout(tick, 55);
            } else {
                s.char -= 1;
                setText(full.slice(0, s.char));
                if (s.char === 0) {
                    s.deleting = false;
                    s.idx = (s.idx + 1) % SAMPLE_QUERIES.length;
                    setTimeout(tick, 350);
                    return;
                }
                setTimeout(tick, 28);
            }
        };
        tick();
        return () => { cancelled = true; };
    }, [isFocused, hasValue]);
    return text;
};

/* Left-rail filters — the whole rail is always visible; the Level,
 *   Content Type, and Department blocks are expanded by default and can
 *   collapse individually. Preferences persist via sessionStorage so
 *   the layout stays "as you left it" this visit. Blocks with more than
 *   3 options collapse the tail behind a "Show more" toggle. */
const FBLOCK_VISIBLE = 3;
const FilterChip = ({ level, setLevel, type, setType, dept, setDept, deptOptions = [], matchCount, totalCount, levelCounts = {}, typeCounts = {}, deptCounts = {} }) => {
    // Default = OPEN. sessionStorage "0" explicitly opts into collapsed.
    const [openLevel, setOpenLevel] = useState(() => {
        try { return sessionStorage.getItem("lh_c2_flt_level") !== "0"; } catch { return true; }
    });
    const [openType, setOpenType] = useState(() => {
        try { return sessionStorage.getItem("lh_c2_flt_type") !== "0"; } catch { return true; }
    });
    const [openDept, setOpenDept] = useState(() => {
        try { return sessionStorage.getItem("lh_c2_flt_dept") !== "0"; } catch { return true; }
    });
    const [seeMoreLevel, setSeeMoreLevel] = useState(false);
    const [seeMoreType, setSeeMoreType] = useState(false);
    const [seeMoreDept, setSeeMoreDept] = useState(false);
    useEffect(() => { try { sessionStorage.setItem("lh_c2_flt_level", openLevel ? "1" : "0"); } catch {} }, [openLevel]);
    useEffect(() => { try { sessionStorage.setItem("lh_c2_flt_type",  openType  ? "1" : "0"); } catch {} }, [openType]);
    useEffect(() => { try { sessionStorage.setItem("lh_c2_flt_dept",  openDept  ? "1" : "0"); } catch {} }, [openDept]);
    const activeCount = (level !== "all" ? 1 : 0) + (type !== "all" ? 1 : 0) + (dept !== "all" ? 1 : 0);
    const clipList = (items, seeMore, activeKey) => {
        if (seeMore || items.length <= FBLOCK_VISIBLE + 1) return items;
        // Always keep the currently-active option visible even if it's past the cut-off.
        const head = items.slice(0, FBLOCK_VISIBLE);
        if (activeKey && activeKey !== "all" && !head.some((x) => x.key === activeKey)) {
            const active = items.find((x) => x.key === activeKey);
            if (active) return [...head.slice(0, FBLOCK_VISIBLE - 1), active];
        }
        return head;
    };
    return (
        <aside className="lh-c2-filters" data-testid="lh-c2-filter-rail">
            <div className="lh-c2-filters-head">
                <h4>Filters</h4>
                {activeCount > 0 && (
                    <button
                        type="button"
                        className="lh-c2-filters-reset lh-c2-filters-reset-inline"
                        onClick={() => { setLevel("all"); setType("all"); setDept("all"); }}
                        data-testid="lh-c2-filter-reset"
                    >Reset</button>
                )}
            </div>
            <div className="lh-c2-fblock">
                <button
                    type="button"
                    className="lh-c2-fblock-head"
                    onClick={() => setOpenDept((v) => !v)}
                    aria-expanded={openDept}
                    data-testid="lh-c2-filter-dept-toggle"
                >
                    <span className="lh-c2-fblock-ic" style={{ background: "#E8F6F5", color: "#2EB4AC" }} aria-hidden><Compass size={12} /></span>
                    <span className="lh-c2-fblock-label">Department</span>
                    {dept !== "all" && <span className="lh-c2-fblock-active-dot" aria-hidden />}
                    <ChevronDown size={12} className={`lh-c2-fblock-chev ${openDept ? "" : "flip"}`} />
                </button>
                {openDept && (
                    <div className="lh-c2-fpill-list">
                        {clipList(deptOptions, seeMoreDept, dept).map((d) => (
                            <button
                                key={d.key}
                                className={`lh-c2-fpill ${dept === d.key ? "active" : ""}`}
                                onClick={() => setDept(d.key)}
                                data-testid={`lh-c2-filter-dept-${d.key}`}
                            >
                                <span className="lh-c2-fpill-ic" style={{ background: "#2EB4AC18", color: "#2EB4AC" }} aria-hidden><Compass size={11} /></span>
                                <span className="lh-c2-fpill-lbl">{d.label}</span>
                                <span className="lh-c2-fpill-count">{deptCounts[d.key] ?? 0}</span>
                            </button>
                        ))}
                        {deptOptions.length > FBLOCK_VISIBLE + 1 && (
                            <button
                                type="button"
                                className="lh-c2-fblock-more"
                                onClick={() => setSeeMoreDept((v) => !v)}
                                data-testid="lh-c2-filter-dept-more"
                            >
                                {seeMoreDept ? "Show less" : `Show ${deptOptions.length - FBLOCK_VISIBLE} more`}
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="lh-c2-fblock">
                <button
                    type="button"
                    className="lh-c2-fblock-head"
                    onClick={() => setOpenLevel((v) => !v)}
                    aria-expanded={openLevel}
                    data-testid="lh-c2-filter-level-toggle"
                >
                    <span className="lh-c2-fblock-ic" style={{ background: "#EEF2FF", color: "#6D5DF3" }} aria-hidden><GraduationCap size={12} /></span>
                    <span className="lh-c2-fblock-label">Level</span>
                    {level !== "all" && <span className="lh-c2-fblock-active-dot" aria-hidden />}
                    <ChevronDown size={12} className={`lh-c2-fblock-chev ${openLevel ? "" : "flip"}`} />
                </button>
                {openLevel && (
                    <div className="lh-c2-fpill-list">
                        {clipList(LEVELS, seeMoreLevel, level).map((l) => {
                            const cnt = levelCounts[l.key] ?? 0;
                            const isEmpty = l.key !== "all" && cnt === 0;
                            return (
                                <button
                                    key={l.key}
                                    className={`lh-c2-fpill ${level === l.key ? "active" : ""} ${isEmpty ? "empty" : ""}`}
                                    onClick={() => setLevel(l.key)}
                                    disabled={isEmpty}
                                    data-testid={`lh-c2-filter-level-${l.key}`}
                                >
                                    <span className="lh-c2-fpill-ic" style={{ background: `${l.tint}18`, color: l.tint }} aria-hidden><l.icon size={11} /></span>
                                    <span className="lh-c2-fpill-lbl">{l.label}</span>
                                    <span className="lh-c2-fpill-count">{cnt}</span>
                                </button>
                            );
                        })}
                        {LEVELS.length > FBLOCK_VISIBLE + 1 && (
                            <button
                                type="button"
                                className="lh-c2-fblock-more"
                                onClick={() => setSeeMoreLevel((v) => !v)}
                                data-testid="lh-c2-filter-level-more"
                            >
                                {seeMoreLevel ? "Show less" : `Show ${LEVELS.length - FBLOCK_VISIBLE} more`}
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="lh-c2-fblock">
                <button
                    type="button"
                    className="lh-c2-fblock-head"
                    onClick={() => setOpenType((v) => !v)}
                    aria-expanded={openType}
                    data-testid="lh-c2-filter-type-toggle"
                >
                    <span className="lh-c2-fblock-ic" style={{ background: "#FEF3E7", color: "#EA580C" }} aria-hidden><Layers size={12} /></span>
                    <span className="lh-c2-fblock-label">Content type</span>
                    {type !== "all" && <span className="lh-c2-fblock-active-dot" aria-hidden />}
                    <ChevronDown size={12} className={`lh-c2-fblock-chev ${openType ? "" : "flip"}`} />
                </button>
                {openType && (
                    <div className="lh-c2-fpill-list">
                        {clipList(TYPES, seeMoreType, type).map((t) => {
                            const cnt = typeCounts[t.key] ?? 0;
                            const isEmpty = t.key !== "all" && cnt === 0;
                            return (
                                <button
                                    key={t.key}
                                    className={`lh-c2-fpill ${type === t.key ? "active" : ""} ${isEmpty ? "empty" : ""}`}
                                    onClick={() => setType(t.key)}
                                    disabled={isEmpty}
                                    data-testid={`lh-c2-filter-type-${t.key}`}
                                >
                                    <span className="lh-c2-fpill-ic" style={{ background: `${t.tint}18`, color: t.tint }} aria-hidden><t.icon size={11} /></span>
                                    <span className="lh-c2-fpill-lbl">{t.label}</span>
                                    <span className="lh-c2-fpill-count">{cnt}</span>
                                </button>
                            );
                        })}
                        {TYPES.length > FBLOCK_VISIBLE + 1 && (
                            <button
                                type="button"
                                className="lh-c2-fblock-more"
                                onClick={() => setSeeMoreType((v) => !v)}
                                data-testid="lh-c2-filter-type-more"
                            >
                                {seeMoreType ? "Show less" : `Show ${TYPES.length - FBLOCK_VISIBLE} more`}
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="lh-c2-fcount" data-testid="lh-c2-filter-count">
                Showing <b>{matchCount}</b> of {totalCount} collections
            </div>
        </aside>
    );
};

/** Hub Assistant panel with session-scoped memory. */
const HubAssistant = ({ open, onOpen, onClose, user, onJumpToFilter }) => {
    const [messages, setMessages] = useState([
        { role: "bot", text: `Hi ${user?.name?.split(" ")[0] || "there"}! I can help you find your way around the Learning Hub — ask me anything, or try one of these.` },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages, busy]);

    const send = async (text) => {
        const q = (text ?? input).trim();
        if (!q || busy) return;
        setMessages((m) => [...m, { role: "you", text: q }]);
        setInput("");
        setBusy(true);
        try {
            // Send the last few exchanges so the assistant has session-scoped memory.
            const history = messages.slice(-6).map((m) => ({ role: m.role === "you" ? "user" : "assistant", content: m.text }));
            const r = await api.post("/learning/assistant", { message: q, history });
            const reply = r.data?.reply || "I couldn't find anything on that just yet — try browsing the shelves below.";
            setMessages((m) => [...m, { role: "bot", text: reply }]);
        } catch {
            setMessages((m) => [...m, { role: "bot", text: "Assistant is offline right now — the shelves below are still fully searchable." }]);
        } finally { setBusy(false); }
    };

    const QUICK_PROMPTS = [
        { label: "Where do I start with Amazon?",  action: () => onJumpToFilter({ deptQuery: "amazon" }) },
        { label: "Show me onboarding resources",   action: () => onJumpToFilter({ level: "beginner" }) },
        { label: "What's new this week?",           action: () => onJumpToFilter({ recent: true }) },
    ];

    if (!open) {
        return (
            <button className="lh-c2-assistant-fab" onClick={onOpen} data-testid="lh-c2-assistant-fab" aria-label="Open Hub Assistant" title="Hub Assistant">
                {/* Robot icon — traced from the concept HTML (line 465) */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="7" width="16" height="12" rx="4"/>
                    <path d="M9 7V5a3 3 0 016 0v2"/>
                    <circle cx="9" cy="13" r="1.2" fill="currentColor" stroke="none"/>
                    <circle cx="15" cy="13" r="1.2" fill="currentColor" stroke="none"/>
                    <path d="M9 16.5c1 .8 5 .8 6 0"/>
                </svg>
            </button>
        );
    }
    return (
        <div className="lh-c2-assistant-panel" data-testid="lh-c2-assistant-panel">
            <div className="lh-c2-assistant-head">
                <div className="lh-c2-assistant-head-title">
                    <span className="lh-c2-assistant-dot" aria-hidden />
                    <b>Hub Assistant</b>
                </div>
                <button className="lh-c2-assistant-close" onClick={onClose} aria-label="Close" data-testid="lh-c2-assistant-close">
                    <X size={14} />
                </button>
            </div>
            <div className="lh-c2-assistant-body" ref={bodyRef}>
                {messages.map((m, i) => (<div key={i} className={`lh-c2-assistant-msg ${m.role}`}>{m.text}</div>))}
                {busy && <div className="lh-c2-assistant-msg bot" style={{ opacity: 0.6 }}>Thinking…</div>}
            </div>
            {/* Persistent suggestions row (concept-parity — sits above the input, always visible). */}
            <div className="lh-c2-assistant-suggestions">
                {QUICK_PROMPTS.map((p, i) => (<button key={i} onClick={p.action} data-testid={`lh-c2-quick-prompt-${i}`}>{p.label}</button>))}
            </div>
            <form className="lh-c2-assistant-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a question…" disabled={busy} data-testid="lh-c2-assistant-input" />
                <button type="submit" disabled={busy || !input.trim()} data-testid="lh-c2-assistant-send" aria-label="Send"><Send size={14} /></button>
            </form>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
const RecycleBinModal = ({ canManage, onClose, onChanged }) => {
    const [items, setItems] = useState({ collections: [], folders: [], resources: [] });
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/learning/recycle");
            setItems(r.data || { collections: [], folders: [], resources: [] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load recycle bin");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const daysLeft = (iso) => {
        if (!iso) return 30;
        const then = new Date(iso).getTime();
        const days = Math.max(0, 30 - Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24)));
        return days;
    };
    const act = async (verb, kind, id) => {
        const label = verb === "restore" ? "Restore" : "Permanently delete";
        if (verb === "purge" && !window.confirm(`${label} this ${kind}? This cannot be undone.`)) return;
        setBusyId(`${kind}-${id}`);
        try {
            if (verb === "restore") await api.post(`/learning/recycle/${kind}/${id}/restore`);
            else await api.delete(`/learning/recycle/${kind}/${id}`);
            toast.success(verb === "restore" ? `${kind} restored` : `${kind} purged`);
            await load();
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
        finally { setBusyId(null); }
    };

    const Section = ({ title, kind, rows, renderMeta }) => (
        <div className="lh-c2-recycle-section">
            <h4>{title} <span className="lh-c2-recycle-count">{rows.length}</span></h4>
            {rows.length === 0 ? (
                <p className="lh-c2-recycle-empty">Nothing here.</p>
            ) : rows.map((r) => (
                <div key={`${kind}-${r.id}`} className="lh-c2-recycle-row" data-testid={`lh-c2-recycle-${kind}-${r.id}`}>
                    <div className="lh-c2-recycle-row-body">
                        <div className="lh-c2-recycle-row-name">{r.name}</div>
                        <div className="lh-c2-recycle-row-meta">{renderMeta(r)} · {daysLeft(r.deleted_at)}d left</div>
                    </div>
                    <div className="lh-c2-recycle-row-actions">
                        <button
                            className="lh-c2m-iconbtn"
                            title="Restore"
                            disabled={busyId === `${kind}-${r.id}`}
                            onClick={() => act("restore", kind, r.id)}
                            data-testid={`lh-c2-recycle-restore-${r.id}`}
                        ><RotateCcw size={12} /></button>
                        <button
                            className="lh-c2m-iconbtn danger"
                            title="Permanently delete"
                            disabled={busyId === `${kind}-${r.id}`}
                            onClick={() => act("purge", kind, r.id)}
                            data-testid={`lh-c2-recycle-purge-${r.id}`}
                        ><Trash2 size={12} /></button>
                    </div>
                </div>
            ))}
        </div>
    );

    return createPortal(
        <div className="lh-c2-modal-backdrop" onClick={onClose} data-testid="lh-c2-recycle-backdrop">
            <div className="lh-c2-newcoll" onClick={(e) => e.stopPropagation()} data-testid="lh-c2-recycle-modal" style={{ maxHeight: "80vh" }}>
                <div className="lh-c2-newcoll-head">
                    <h3>Recycle bin</h3>
                    <button type="button" className="lh-c2-modal-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="lh-c2-newcoll-body">
                    <p style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: -4 }}>
                        Items sit here for 30 days after deletion. Restore to bring them back or purge to remove for good.
                    </p>
                    {loading ? (
                        <p style={{ color: "var(--ink-soft)" }}>Loading…</p>
                    ) : (
                        <>
                            <Section
                                title="Collections"
                                kind="collection"
                                rows={items.collections.filter(canManage)}
                                renderMeta={(r) => r.owner_department_name || "Collection"}
                            />
                            <Section
                                title="Folders"
                                kind="folder"
                                rows={items.folders}
                                renderMeta={(r) => r._collection_name || "Folder"}
                            />
                            <Section
                                title="Resources"
                                kind="resource"
                                rows={items.resources}
                                renderMeta={(r) => `${r._collection_name || "Resource"} · ${r.type || "file"}`}
                            />
                        </>
                    )}
                </div>
                <div className="lh-c2-newcoll-footer">
                    <button type="button" className="lh-c2-btn" onClick={onClose} data-testid="lh-c2-recycle-close">Close</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

/** Concept-2 "New Resource" dialog — creates a standalone (loose)
 *  resource that lives outside any collection. Posts to /resources/loose.
 *  Uses the same field-set as the in-modal Add Resource form. */
// Google Gemini's signature 4-point sparkle. Small inline SVG with a
// soft multi-hue drop-shadow "glow" that gently pulses so it reads as
// "Gemini is thinking / painting". Used in the reroll toast + dialog
// header, without any 🍌.
const GeminiIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden className="lh-c2-gem-icon" style={{ verticalAlign: "-2px" }}>
        <defs>
            <linearGradient id="lh_c2_gem_grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%"  stopColor="#4285F4" />
                <stop offset="45%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
        </defs>
        <path fill="url(#lh_c2_gem_grad)" d="M12 2 L13.4 8.6 A5 5 0 0 0 15.4 10.6 L22 12 L15.4 13.4 A5 5 0 0 0 13.4 15.4 L12 22 L10.6 15.4 A5 5 0 0 0 8.6 13.4 L2 12 L8.6 10.6 A5 5 0 0 0 10.6 8.6 Z" />
    </svg>
);

const RerollCoverDialog = ({ collection, onClose, onGenerate }) => {
    const [prompt, setPrompt] = React.useState("");
    if (!collection) return null;
    return createPortal(
        <div className="lh-c2-modal-backdrop" onClick={onClose} data-testid="lh-c2-reroll-backdrop">
            <div className="lh-c2-newcoll" onClick={(e) => e.stopPropagation()} style={{ width: 520 }} data-testid="lh-c2-reroll-dialog">
                <div className="lh-c2-newcoll-head">
                    <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <GeminiIcon size={18} /> Reroll cover for "{collection.name}"
                    </h3>
                    <button type="button" className="lh-c2-modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                <div className="lh-c2-newcoll-body">
                    <p style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12 }}>
                        Nano Banana will paint a fresh cover based on the collection name. Add a nudge below if you want to steer it (optional).
                    </p>
                    <div className="lh-c2-newcoll-field">
                        <label className="lh-c2-newcoll-label">Custom prompt (optional)</label>
                        <textarea
                            className="lh-c2-newcoll-input"
                            rows={3}
                            value={prompt}
                            placeholder={`e.g. "warehouse workers scanning boxes with a subtle teal circuit pattern"`}
                            onChange={(e) => setPrompt(e.target.value)}
                            data-testid="lh-c2-reroll-prompt"
                            autoFocus
                        />
                    </div>
                </div>
                <div className="lh-c2-newcoll-footer">
                    <button type="button" className="lh-c2-btn" onClick={onClose} data-testid="lh-c2-reroll-cancel">Cancel</button>
                    <button
                        type="button"
                        className="lh-c2-btn lh-c2-btn-solid"
                        onClick={() => onGenerate(collection, prompt)}
                        data-testid="lh-c2-reroll-generate"
                    >
                        <Sparkles size={13} /> Generate
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

const NewResourceDialog = ({ onClose, onCreated }) => {
    const [values, setValues] = useState({ name: "", type: "article", level: "beginner", url: "", duration_minutes: "", overview: "", learning_outcomes: "", notes: "", file: "" });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const uploadFile = async (f) => {
        if (!f) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", f);
            const r = await api.post("/learning/upload", form);
            setValues((v) => ({ ...v, file: { storage_path: r.data.storage_path, file_original_name: r.data.original_filename, content_type: r.data.content_type, size_bytes: r.data.size_bytes } }));
            toast.success(`Uploaded ${r.data.original_filename}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };
    const submit = async (e) => {
        e?.preventDefault?.();
        if (!values.name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const payload = {
                name: values.name.trim(),
                type: values.type,
                level: values.level,
                url: values.url || "",
                duration_minutes: values.duration_minutes ? Number(values.duration_minutes) : null,
                overview: values.overview || "",
                learning_outcomes: (values.learning_outcomes || "").split("\n").map((s) => s.trim()).filter(Boolean),
                notes: values.notes || "",
            };
            if (values.file && typeof values.file === "object" && values.file.storage_path) {
                payload.storage_path = values.file.storage_path;
                payload.file_original_name = values.file.file_original_name;
                payload.content_type = values.file.content_type;
                payload.size_bytes = values.file.size_bytes;
            }
            await api.post("/learning/resources/loose", payload);
            toast.success(`Resource "${values.name.trim()}" created`);
            onCreated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create resource");
        } finally { setBusy(false); }
    };
    return createPortal(
        <div className="lh-c2-modal-backdrop" onClick={onClose} data-testid="lh-c2-newres-backdrop">
            <form className="lh-c2-newcoll lh-c2-newcoll-wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="lh-c2-newres">
                <div className="lh-c2-newcoll-head">
                    <h3>New resource</h3>
                    <button type="button" className="lh-c2-modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                <div className="lh-c2-newcoll-body lh-c2-newcoll-grid">
                    {[
                        { key: "name", label: "Title", placeholder: "e.g. Amazon PPC quick primer", autoFocus: true, col: "full" },
                        { key: "type", label: "Type", type: "select", options: [
                            { value: "article", label: "Article" }, { value: "video", label: "Video" }, { value: "doc", label: "Doc" },
                            { value: "course", label: "Course" }, { value: "podcast", label: "Podcast" }, { value: "quiz", label: "Quiz" }, { value: "book", label: "Book" }, { value: "file", label: "File" },
                        ] },
                        { key: "level", label: "Level", type: "select", options: [
                            { value: "beginner", label: "Beginner" }, { value: "intermediate", label: "Intermediate" }, { value: "advanced", label: "Advanced" },
                        ] },
                        { key: "url", label: "Source URL (optional)", placeholder: "https://…" },
                        { key: "duration_minutes", label: "Duration (min, optional)", type: "number", placeholder: "5" },
                        { key: "file", label: "Or upload a file (any format — opens in a new tab)", type: "file", col: "full" },
                        { key: "overview", label: "Overview (optional)", type: "textarea", rows: 3, placeholder: "One paragraph on what this covers…", col: "full" },
                        { key: "learning_outcomes", label: "Learning outcomes (one per line, optional)", type: "textarea", rows: 3, placeholder: "Understand X\nApply Y", col: "full" },
                        { key: "notes", label: "Notes (optional)", type: "textarea", rows: 2, placeholder: "Any usage instructions…", col: "full" },
                    ].map((f) => (
                        <div className={`lh-c2-newcoll-field ${f.col === "full" ? "lh-c2-newcoll-field-full" : ""}`} key={f.key}>
                            <label className="lh-c2-newcoll-label">{f.label}</label>
                            {f.type === "textarea" ? (
                                <textarea className="lh-c2-newcoll-input" rows={f.rows || 3} value={values[f.key]} placeholder={f.placeholder} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} data-testid={`lh-c2-newres-${f.key}`} />
                            ) : f.type === "select" ? (
                                <select className="lh-c2-newcoll-input" value={values[f.key]} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} data-testid={`lh-c2-newres-${f.key}`}>
                                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            ) : f.type === "file" ? (
                                <div className="lh-c2-qf-file" data-testid={`lh-c2-newres-${f.key}-wrap`}>
                                    {values.file && typeof values.file === "object" && values.file.storage_path ? (
                                        <div className="lh-c2-qf-file-pill">
                                            <FileText size={14} />
                                            <span className="lh-c2-qf-file-name">{values.file.file_original_name}</span>
                                            <button type="button" className="lh-c2-qf-file-x" onClick={() => setValues((v) => ({ ...v, file: "" }))} aria-label="Remove file" data-testid={`lh-c2-newres-${f.key}-clear`}><X size={13} /></button>
                                        </div>
                                    ) : (
                                        <label className="lh-c2-qf-file-drop">
                                            <UploadCloud size={16} />
                                            <span>{uploading ? "Uploading…" : "Choose any file — PDF, DOC, PPT, image…"}</span>
                                            <input type="file" hidden onChange={(e) => uploadFile(e.target.files?.[0])} data-testid={`lh-c2-newres-${f.key}`} />
                                        </label>
                                    )}
                                </div>
                            ) : (
                                <input className="lh-c2-newcoll-input" type={f.type || "text"} value={values[f.key]} placeholder={f.placeholder} autoFocus={f.autoFocus} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} data-testid={`lh-c2-newres-${f.key}`} />
                            )}
                        </div>
                    ))}
                </div>
                <div className="lh-c2-newcoll-footer">
                    <button type="button" className="lh-c2-btn" onClick={onClose} data-testid="lh-c2-newres-cancel">Cancel</button>
                    <button type="submit" className="lh-c2-btn lh-c2-btn-solid" disabled={busy || uploading || !values.name.trim()} data-testid="lh-c2-newres-submit">
                        {busy ? "Saving…" : "Create resource"}
                    </button>
                </div>
            </form>
        </div>,
        document.body,
    );
};

/** Compact "Assignments" panel — floats to the right of the learning
 *  library section. Toggle button lives in the library head. Two tabs:
 *  Assigned to me (default) and Assigned by me. Renders both collections
 *  and resources per tab, up to 8 items each. */
const AssignmentsPanel = ({ open, onClose, tab, onTabChange, data, onOpenCollection, onOpenResource }) => {
    if (!open) return null;
    const showTo = tab === "to";
    const cols = showTo ? data.collections_to_me : data.collections_by_me;
    const res  = showTo ? data.resources_to_me   : data.resources_by_me;
    const totalCount = (cols?.length || 0) + (res?.length || 0);
    return createPortal(
        <div className="lh-c2-assign-panel lh-c2" data-testid="lh-c2-assignments-panel">
            <div className="lh-c2-assign-panel-head">
                <div>
                    <h4>Handoffs</h4>
                    <p>{totalCount} item{totalCount === 1 ? "" : "s"} in flight</p>
                </div>
                <button className="lh-c2-modal-close" onClick={onClose} aria-label="Close" data-testid="lh-c2-assignments-close">
                    <X size={14} />
                </button>
            </div>
            <div className="lh-c2-assign-panel-tabs">
                <button className={showTo ? "active" : ""} onClick={() => onTabChange("to")} data-testid="lh-c2-assignments-tab-to">Assigned to me</button>
                <button className={!showTo ? "active" : ""} onClick={() => onTabChange("by")} data-testid="lh-c2-assignments-tab-by">Assigned by me</button>
            </div>
            <div className="lh-c2-assign-panel-body">
                {totalCount === 0 && <p className="lh-c2-assign-panel-empty">Nothing here yet.</p>}
                {(cols || []).slice(0, 8).map((c) => (
                    <button
                        key={`c-${c.id}`}
                        type="button"
                        className="lh-c2-assign-panel-row"
                        onClick={() => onOpenCollection?.(c)}
                        data-testid={`lh-c2-handoff-coll-${c.id}`}
                    >
                        <span className="lh-c2m-pill" style={{ background: "#5B7FDE", width: 20, height: 20, fontSize: 8 }}>CL</span>
                        <div className="lh-c2-assign-panel-body-text">
                            <div className="lh-c2-assign-panel-name">{c.name}</div>
                            <div className="lh-c2-assign-panel-sub">{showTo ? `by ${c.assigned_by_name || "someone"}` : `to ${(c.assigned_to_names || []).join(", ") || "—"}`}</div>
                        </div>
                    </button>
                ))}
                {(res || []).slice(0, 8).map((r) => (
                    <button
                        key={`r-${r.id}`}
                        type="button"
                        className="lh-c2-assign-panel-row"
                        onClick={() => onOpenResource?.(r)}
                        data-testid={`lh-c2-handoff-res-${r.id}`}
                    >
                        <span className="lh-c2m-pill" style={{ background: "#E7A33E", width: 20, height: 20, fontSize: 8 }}>{(r.type || "AR").slice(0, 2).toUpperCase()}</span>
                        <div className="lh-c2-assign-panel-body-text">
                            <div className="lh-c2-assign-panel-name">
                                {r.name}
                                {r.storage_missing && (
                                    <span className="lh-c2-missing-pill lh-c2-missing-pill-sm" title="File missing — re-upload">
                                        <AlertTriangle size={9} />
                                    </span>
                                )}
                            </div>
                            <div className="lh-c2-assign-panel-sub">{showTo ? `by ${r.assigned_by_name || "someone"}` : `to ${(r.assigned_to_names || []).join(", ") || "—"}`}</div>
                        </div>
                    </button>
                ))}
            </div>
        </div>,
        document.body,
    );
};

const LearningHubConcept2 = () => {
    const { user } = useAuth();
    const { depts, settings } = useData();
    const autoCovers = !!settings?.learning_auto_covers;
    const [collections, setCollections] = useState([]);
    const [activeCollection, setActiveCollection] = useState(null);
    const [activeResource, setActiveResource] = useState(null);
    const [editingCollection, setEditingCollection] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    const [showCreate, setShowCreate] = useState(false);
    const [showWelcome, setShowWelcome] = useState(false);
    const [showRecycle, setShowRecycle] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [showNewResource, setShowNewResource] = useState(false);
    const [newMenuOpen, setNewMenuOpen] = useState(false);
    const [looseResources, setLooseResources] = useState([]);
    const [assignments, setAssignments] = useState({ collections_to_me: [], collections_by_me: [], resources_to_me: [], resources_by_me: [] });
    const [showAssignPanel, setShowAssignPanel] = useState(false);
    const [assignPanelTab, setAssignPanelTab] = useState("to");
    const [assigningItem, setAssigningItem] = useState(null); // { kind, id, name, assigned_to }
    const [assistantOpen, setAssistantOpen] = useState(false);
    const [filterLevel, setFilterLevel] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [filterDept, setFilterDept] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchFocused, setSearchFocused] = useState(false);
    // AI Search state — Claude Sonnet 5-backed ranking. Fires 500 ms after
    // the user pauses at 4+ chars OR when they click the "Ask AI" pill.
    const [aiResults, setAiResults] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiCached, setAiCached] = useState(false);
    const [aiError, setAiError] = useState(null);
    const aiSeq = useRef(0);
    const [expandedDepts, setExpandedDepts] = useState({}); // deptId -> bool, expands "View all" inline
    const [searchStuck, setSearchStuck] = useState(false);
    const [searchHidden, setSearchHidden] = useState(false);
    const sentinelRef = useRef(null);
    const typewriterText = useTypewriterPlaceholder(searchFocused, !!searchQuery);

    /* ── Sticky search behaviour (mockup lines 45-49, 1435-1456):
     *  1) `is-stuck` toggles when the sentinel above the search leaves
     *     the viewport (drops the eyebrow, adds shadow to feel pinned).
     *  2) `nav-hidden` slides it up when scrolling DOWN past 160px, and
     *     brings it back when scrolling UP. */
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const obs = new IntersectionObserver(
            ([entry]) => setSearchStuck(!entry.isIntersecting),
            { threshold: 0 },
        );
        obs.observe(sentinel);
        return () => obs.disconnect();
    }, []);
    useEffect(() => {
        let lastY = window.scrollY;
        let ticking = false;
        const update = () => {
            const y = window.scrollY;
            const down = y > lastY + 4;
            const up = y < lastY - 4;
            const past = y > 160;
            if (down && past) setSearchHidden(true);
            else if (up || !past) setSearchHidden(false);
            lastY = y;
            ticking = false;
        };
        const onScroll = () => {
            if (!ticking) { requestAnimationFrame(update); ticking = true; }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        try {
            if (!sessionStorage.getItem("lh_c2_welcomed") && user?.name) setShowWelcome(true);
        } catch { /* ignore */ }
    }, [user?.name]);
    const dismissWelcome = () => {
        try { sessionStorage.setItem("lh_c2_welcomed", "1"); } catch { /* ignore */ }
        setShowWelcome(false);
    };

    const load = async () => {
        try {
            const [colls, loose, assigns] = await Promise.all([
                api.get("/learning/collections"),
                api.get("/learning/resources/loose").catch(() => ({ data: [] })),
                api.get("/learning/assignments").catch(() => ({ data: null })),
            ]);
            setCollections(colls.data || []);
            setLooseResources(loose.data || []);
            if (assigns.data) setAssignments(assigns.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load collections");
        }
    };
    useEffect(() => { load(); }, []);

    // When landing on /learning?collection=<id>, auto-open that collection's
    // modal once the collections list has loaded so the "Open in new tab"
    // affordance actually lands on the right screen. `once` guards against
    // repeat pops if the list refreshes.
    const autoOpenedRef = useRef(false);
    useEffect(() => {
        if (autoOpenedRef.current || !collections.length) return;
        const params = new URLSearchParams(window.location.search);
        const cid = params.get("collection");
        if (!cid) return;
        const hit = collections.find((c) => c.id === cid);
        if (hit) {
            setActiveCollection(hit);
            autoOpenedRef.current = true;
        }
    }, [collections]);

    const myDeptId = user?.department_id;
    const myDeptName = useMemo(() => {
        const d = (depts || []).find((x) => x.id === myDeptId);
        return d?.name || "My department";
    }, [depts, myDeptId]);

    // REAL filters — use level/type distributions from the collection payload.
    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return collections.filter((c) => {
            if (filterDept !== "all" && c.owner_department_id !== filterDept) return false;
            if (q && !`${c.name || ""} ${c.description || ""} ${c.owner_department_name || ""}`.toLowerCase().includes(q)) return false;
            if (filterLevel !== "all") {
                const dist = c.level_distribution || {};
                if (!(dist[filterLevel] > 0)) return false;
            }
            if (filterType !== "all") {
                const dist = c.type_distribution || {};
                if (!(dist[filterType] > 0)) return false;
            }
            return true;
        });
    }, [collections, filterDept, filterLevel, filterType, searchQuery]);

    // Smooth-scroll to the first matching shelf whenever a filter is
    // applied (or changed) so the user can visually confirm the pick.
    // Adds a soft teal ring pulse to the target shelf for ~1.5s.
    const firstFilterRun = useRef(true);
    useEffect(() => {
        if (firstFilterRun.current) { firstFilterRun.current = false; return; }
        const noneActive = filterDept === "all" && filterLevel === "all" && filterType === "all";
        if (noneActive) return;
        // Slight defer so the DOM has painted the newly-filtered shelves.
        const t = setTimeout(() => {
            const container = document.getElementById("lh-c2-library");
            const shelf = container?.querySelector('[data-testid^="lh-c2-dept-"]')
                || document.querySelector('[data-testid="lh-c2-mydept"]');
            if (!shelf) return;
            shelf.scrollIntoView({ behavior: "smooth", block: "start" });
            shelf.classList.add("lh-c2-filter-pulse");
            setTimeout(() => shelf.classList.remove("lh-c2-filter-pulse"), 1600);
        }, 60);
        return () => clearTimeout(t);
    }, [filterDept, filterLevel, filterType]);

    // Filter pill counts.
    // Dept counts always reflect the FULL corpus (they don't depend on
    // level/type). Level & Type counts are DEPENDENT on the currently
    // selected department — pick a dept and the sub-filters recompute
    // to only show what actually lives inside that dept.
    const { levelCounts, typeCounts, deptCounts, deptOptions } = useMemo(() => {
        const lvl = { all: 0 }, typ = { all: 0 };
        LEVELS.forEach((l) => { if (l.key !== "all") lvl[l.key] = 0; });
        TYPES.forEach((t) => { if (t.key !== "all") typ[t.key] = 0; });
        const dpt = { all: 0 };
        const dptNames = {}; // id -> {name, alias}
        for (const c of collections) {
            const did = c.owner_department_id || "unassigned";
            dpt[did] = (dpt[did] || 0) + 1;
            if (!dptNames[did]) dptNames[did] = { name: c.owner_department_name || "Unassigned", alias: c.owner_department_alias || c.owner_department_name || "Unassigned" };
            // Level/Type totals scoped by the currently-selected department.
            if (filterDept !== "all" && did !== filterDept) continue;
            const ld = c.level_distribution || {};
            const td = c.type_distribution || {};
            for (const k of Object.keys(ld)) if (k in lvl) lvl[k] += ld[k] || 0;
            for (const k of Object.keys(td)) if (k in typ) typ[k] += td[k] || 0;
        }
        lvl.all = Object.entries(lvl).reduce((s, [k, v]) => k === "all" ? s : s + v, 0);
        typ.all = Object.entries(typ).reduce((s, [k, v]) => k === "all" ? s : s + v, 0);
        dpt.all = collections.length;
        const opts = [{ key: "all", label: "All departments" }].concat(
            Object.entries(dptNames)
                .map(([id, meta]) => ({ key: id, label: meta.alias || meta.name }))
                .sort((a, b) => (a.label || "").localeCompare(b.label || "")),
        );
        return { levelCounts: lvl, typeCounts: typ, deptCounts: dpt, deptOptions: opts };
    }, [collections, filterDept]);

    // When a user picks a department that doesn't have the currently
    // active Level or Type, silently reset those sub-filters so the
    // shelves don't become empty for no visible reason.
    useEffect(() => {
        if (filterLevel !== "all" && !(levelCounts[filterLevel] > 0)) setFilterLevel("all");
        if (filterType !== "all" && !(typeCounts[filterType] > 0)) setFilterType("all");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDept, levelCounts, typeCounts]);

    const myDeptCollections = filtered.filter((c) => c.owner_department_id === myDeptId);
    const otherDepts = useMemo(() => {
        const byDept = {};
        for (const c of filtered) {
            if (c.owner_department_id === myDeptId) continue;
            const key = c.owner_department_id || "unassigned";
            (byDept[key] = byDept[key] || { id: c.owner_department_id, name: c.owner_department_name || "Unassigned", alias: c.owner_department_alias || c.owner_department_name || "Unassigned", collections: [] }).collections.push(c);
        }
        return Object.values(byDept).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [filtered, myDeptId]);

    const handleJumpToFilter = ({ deptQuery, level, recent }) => {
        if (level) setFilterLevel(level);
        if (deptQuery) {
            // Dept filter was retired from the UI (mockup only has Level + Type).
            // Fall back to seeding the search box so the shelf still filters by
            // dept name via the free-text search matcher.
            setSearchQuery(deptQuery);
        }
        if (recent) setSearchQuery("");
        setAssistantOpen(false);
        setTimeout(() => document.getElementById("lh-c2-library")?.scrollIntoView({ behavior: "smooth" }), 100);
    };

    /* ── canManage: super_admin sees Edit/Delete on every card. Owner of
     *   the collection sees them on their own. Manager of the owner's
     *   department also sees them (same rule as the backend gate). */
    const canManageCollection = (c) => {
        if (!user || !c) return false;
        if (user.role === "super_admin") return true;
        if (c.owner_id && c.owner_id === user.user_id) return true;
        // Managers of the owning department can curate.
        if (user.role === "manager" && user.department_id && c.owner_department_id === user.department_id) return true;
        // Any teammate in the owning department can curate the shared
        // learning surface (edit / add / delete / move / assign). Auth is
        // still enforced globally by Google OAuth + domain allow-list.
        if (user.department_id && c.owner_department_id && c.owner_department_id === user.department_id) return true;
        return false;
    };
    const canManageResource = (r, c) => {
        if (!user || !r) return false;
        if (user.role === "super_admin") return true;
        if (r.created_by && r.created_by === user.user_id) return true;
        if (c && canManageCollection(c)) return true;
        return false;
    };
    const toggleDeptExpanded = (deptId) => setExpandedDepts((s) => ({ ...s, [deptId]: !s[deptId] }));
    const openAssignForCollection = (c) => setAssigningItem({ kind: "collection", id: c.id, name: c.name, assigned_to: c.assigned_to || [] });

    /* Opens the Handoffs panel. If it's empty on first open, kicks off
     * the idempotent seed so users see a populated example immediately.
     * The seed endpoint records `_seeded_by_user` so it never fires
     * twice for the same account. */
    const openHandoffs = async () => {
        const empty = (assignments.collections_to_me?.length || 0)
            + (assignments.collections_by_me?.length || 0)
            + (assignments.resources_to_me?.length || 0)
            + (assignments.resources_by_me?.length || 0) === 0;
        setShowAssignPanel((v) => !v);
        // The floating panel is anchored to the viewport (fixed top-right).
        // Scroll the library header back into view so the panel + its
        // origin button stay visible together instead of appearing "above".
        if (!showAssignPanel) {
            setTimeout(() => document.getElementById("lh-c2-library")?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
        }
        if (empty && !showAssignPanel) {
            try {
                const r = await api.post("/learning/assignments/seed");
                if (r.data?.seeded) {
                    toast.info("Populated sample handoffs so you can see the panel");
                    await load();
                }
            } catch { /* silent — panel opens anyway */ }
        }
    };

    /* Drag-and-drop reorder for collections within a single dept shelf.
     * The parent owns the source-of-truth list, so we splice locally then
     * fire PUT /learning/collections/reorder with the new order-index list. */
    const reorderCollectionsInShelf = async (shelfCollections, fromIdx, toIdx) => {
        const nextShelf = [...shelfCollections];
        const [moved] = nextShelf.splice(fromIdx, 1);
        nextShelf.splice(toIdx, 0, moved);
        const orderById = new Map(nextShelf.map((c, i) => [c.id, i]));
        setCollections((prev) => {
            // Re-emit the global list with the new order for the affected ids
            // preserved; untouched ids keep their existing order_index.
            const remixed = prev.map((c) => (orderById.has(c.id) ? { ...c, order_index: orderById.get(c.id) } : c));
            return [...remixed].sort((a, b) => (a.order_index ?? 9999) - (b.order_index ?? 9999));
        });
        try {
            await api.put("/learning/collections/reorder", {
                collection_ids: nextShelf.map((c) => c.id),
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reorder failed");
            await load();
        }
    };
    const deleteCollection = async (c) => {
        if (!window.confirm(`Move "${c.name}" to the Recycle bin? Every folder + resource inside will move too. You can restore within 30 days.`)) return;
        try {
            await api.delete(`/learning/collections/${c.id}`);
            setCollections((prev) => prev.filter((x) => x.id !== c.id));
            toast.success("Collection moved to Recycle bin");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const [rerollFor, setRerollFor] = useState(null);
    // Covers manually generated in *this session* stay visible even when
    // the org has auto-covers disabled — otherwise the user would generate
    // a fresh Nano Banana image and never see it.
    const [freshCovers, setFreshCovers] = useState(() => new Set());
    const rerollCover = (c) => setRerollFor(c);
    const doReroll = async (c, customPrompt) => {
        const toastId = toast.loading(
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <GeminiIcon size={14} /> Gemini is painting your cover…
            </span>,
            { duration: 30000 },
        );
        setRerollFor(null);
        try {
            const r = await api.post(`/learning/collections/${c.id}/reroll-cover`, { custom_prompt: customPrompt || "" });
            setCollections((prev) => prev.map((x) => (x.id === c.id ? { ...x, cover_image: r.data.cover_image } : x)));
            setFreshCovers((prev) => { const next = new Set(prev); next.add(c.id); return next; });
            toast.success("Cover ready ✨", { id: toastId });
        } catch (e) { toast.error(e?.response?.data?.detail || "Reroll failed", { id: toastId }); }
    };

    /* AI Search — Claude Sonnet 5 ranked recommendations. `manual=true` is
     * the explicit "Ask AI" click; auto-fires (typed 4+ chars, paused
     * 500 ms) also go through this so the panel state stays consistent.
     * `aiSeq` guards against stale responses if the user keeps typing —
     * only the latest request updates the panel. */
    const runAiSearch = async (q, manual = false) => {
        const query = (q || "").trim();
        if (query.length < 4) {
            setAiResults([]); setAiOpen(false); setAiError(null); setAiLoading(false);
            return;
        }
        const mySeq = ++aiSeq.current;
        setAiLoading(true);
        setAiError(null);
        if (manual) setAiOpen(true);
        try {
            const resp = await api.post("/learning/ai-search", { query, deep: true });
            if (mySeq !== aiSeq.current) return; // stale
            setAiResults(resp.data.results || []);
            setAiCached(!!resp.data.cached);
            setAiOpen(true);
        } catch (e) {
            if (mySeq !== aiSeq.current) return;
            setAiError(e?.response?.data?.detail || "AI search is temporarily unavailable");
            setAiResults([]);
            setAiOpen(true);
        } finally {
            if (mySeq === aiSeq.current) setAiLoading(false);
        }
    };
    // Debounced auto-trigger: 500 ms after last keystroke, at 4+ chars.
    useEffect(() => {
        const q = searchQuery.trim();
        if (q.length < 4) {
            setAiResults([]); setAiOpen(false); setAiError(null);
            return;
        }
        const t = setTimeout(() => { runAiSearch(q, false); }, 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const openAiResult = async (item) => {
        setAiOpen(false);
        if (item.kind === "resource") {
            let full = looseResources.find((x) => x.id === item.id);
            if (!full) {
                try {
                    const resp = await api.get(`/learning/resources/${item.id}`);
                    full = resp.data;
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't open resource");
                    return;
                }
            }
            const col = full.collection_id ? (collections.find((x) => x.id === full.collection_id) || null) : null;
            setActiveResource({ resource: full, collection: col });
        } else if (item.kind === "collection") {
            const full = collections.find((x) => x.id === item.id) || { id: item.id, name: item.name };
            setActiveCollection(full);
        } else if (item.kind === "folder" && item.collection_id) {
            const full = collections.find((x) => x.id === item.collection_id) || { id: item.collection_id, name: item.collection_name };
            setActiveCollection(full);
        }
    };

    return (
        <div className="lh-c2" data-testid="lh-c2-root">
            {/* Hero with slideshow */}
            <section className="lh-c2-hero">
                <div className="lh-c2-wrap">
                    <div className="lh-c2-hero-flex">
                        <div className="lh-c2-hero-copy">
                            <div className="lh-c2-hero-eyebrow">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
                            <h1>To Odyssey<br/>Learning Hub</h1>
                            <p>Everything you need to grow with confidence — all in one place. Explore team-made playbooks, courses, videos and curated collections across every department at Wellversed.</p>
                            <button className="lh-c2-hero-cta" onClick={() => document.getElementById("lh-c2-library")?.scrollIntoView({ behavior: "smooth" })} data-testid="lh-c2-get-started">Get started</button>
                        </div>
                        <Slideshow />
                    </div>
                </div>
            </section>

            {/* Sentinel: when it exits viewport, `is-stuck` kicks in on the search bar. */}
            <div ref={sentinelRef} className="lh-c2-grand-search-sentinel" aria-hidden />
            {/* Grand search — mockup-parity: eyebrow + big search bar with typewriter placeholder */}
            <div className={`lh-c2-wrap lh-c2-grand-search lh-c2-grand-search-top ${searchStuck ? "is-stuck" : ""} ${searchHidden ? "nav-hidden" : ""}`}>
                <div className="lh-c2-grand-search-eyebrow">Find anything, across every department</div>
                <div className={`lh-c2-grand-search-box ${searchFocused ? "focused" : ""}`}>
                    <Search size={20} />
                    <div className="lh-c2-grand-search-input-wrap">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setSearchFocused(true)}
                            onBlur={() => setSearchFocused(false)}
                            onKeyDown={(e) => { if (e.key === "Enter") runAiSearch(searchQuery, true); }}
                            data-testid="lh-c2-grand-search"
                        />
                        {!searchQuery && !searchFocused && (
                            <div className="lh-c2-grand-search-fake" aria-hidden data-testid="lh-c2-typewriter">
                                <span>{typewriterText}</span>
                                <span className="lh-c2-caret">|</span>
                            </div>
                        )}
                        {!searchQuery && searchFocused && (
                            <div className="lh-c2-grand-search-fake muted" aria-hidden>Find anything, across every department</div>
                        )}
                    </div>
                    <button
                        type="button"
                        className="lh-c2-ai-btn"
                        onClick={() => runAiSearch(searchQuery, true)}
                        disabled={aiLoading || searchQuery.trim().length < 4}
                        title={searchQuery.trim().length < 4 ? "Type at least 4 characters" : "Ask AI to find matches"}
                        data-testid="lh-c2-ai-search-btn"
                    >
                        {aiLoading ? <RotateCcw size={13} className="lh-c2-ai-spin" /> : <Sparkles size={13} />}
                        Ask AI
                    </button>
                    <span className="lh-c2-grand-search-hint">↵</span>
                </div>
                {aiOpen && (
                    <AiSearchPanel
                        loading={aiLoading}
                        results={aiResults}
                        error={aiError}
                        cached={aiCached}
                        query={searchQuery}
                        onOpen={openAiResult}
                        onClose={() => setAiOpen(false)}
                    />
                )}
            </div>

            <div className="lh-c2-wrap">
                {/* My Department */}
                {myDeptCollections.length > 0 ? (
                    <div className="lh-c2-mydept" data-testid="lh-c2-mydept">
                        <div className="lh-c2-mydept-badge-row">
                            <span className="lh-c2-mydept-badge"><Star size={12} fill="currentColor" /> My Department</span>
                            <button
                                className="lh-c2-mydept-badge lh-c2-mydept-badge-btn"
                                onClick={() => {
                                    setViewMode("mindmap");
                                    // Wait a frame for the mind-map DOM to
                                    // mount, then smooth-scroll to it so the
                                    // user lands directly on the tree.
                                    setTimeout(() => {
                                        const el = document.querySelector('[data-testid="lh-c2-mindmap-shell"]');
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 80);
                                }}
                                title="Open the mind map view scoped to your department"
                                data-testid="lh-c2-mydept-mindmap"
                            >
                                <GitBranch size={12} /> Mind map
                            </button>
                        </div>
                        <div className="lh-c2-dept-row-head">
                            <div className="lh-c2-dept-row-head-left">
                                <h3>{myDeptName}</h3>
                                <span className="count">{myDeptCollections.length} collection{myDeptCollections.length === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                        <MyDeptShelf
                            collections={expandedDepts[myDeptId] ? myDeptCollections : myDeptCollections.slice(0, 4)}
                            canManageCollection={canManageCollection}
                            searchQuery={searchQuery}
                            reorderCollectionsInShelf={reorderCollectionsInShelf}
                            setActiveCollection={setActiveCollection}
                            setEditingCollection={setEditingCollection}
                            deleteCollection={deleteCollection}
                            openAssignForCollection={openAssignForCollection}
                            rerollCover={rerollCover}
                            autoCovers={autoCovers}
                            freshCovers={freshCovers}
                        />
                        {myDeptCollections.length > 4 && myDeptId && (
                            <div className="lh-c2-viewall-row">
                                <ViewAllButton
                                    deptId={myDeptId}
                                    count={myDeptCollections.length}
                                    expanded={!!expandedDepts[myDeptId]}
                                    onExpand={() => toggleDeptExpanded(myDeptId)}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="lh-c2-mydept" data-testid="lh-c2-mydept-empty">
                        <span className="lh-c2-mydept-badge"><Star size={12} fill="currentColor" /> My Department</span>
                        <h3 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, marginTop: 4 }}>{myDeptName}</h3>
                        <p style={{ color: "var(--ink-soft)", fontSize: 13.5, marginTop: 6, marginBottom: 12 }}>
                            Your department doesn't have any collections yet. Kick things off by creating the first one.
                        </p>
                        <button className="lh-c2-btn lh-c2-btn-solid" onClick={() => setShowCreate(true)} data-testid="lh-c2-mydept-new"><Plus size={14} /> New Collection</button>
                    </div>
                )}

                {/* Library section */}
                <section className="lh-c2-lib" id="lh-c2-library">
                    <div className="lh-c2-lib-head">
                        <div>
                            <h2>Learning Library by Department</h2>
                            <p>Every collection across Wellversed, organized the way your teams actually work.</p>
                        </div>
                        <div className="lh-c2-lib-head-actions">
                            <div className="lh-c2-new-menu" data-testid="lh-c2-new-menu">
                                <button
                                    className="lh-c2-btn lh-c2-btn-solid"
                                    onClick={() => setNewMenuOpen((v) => !v)}
                                    data-testid="lh-c2-new-menu-btn"
                                    aria-expanded={newMenuOpen}
                                ><Plus size={14} /> New <ChevronDown size={13} /></button>
                                {newMenuOpen && (
                                    <div className="lh-c2-new-menu-pop" onMouseLeave={() => setNewMenuOpen(false)}>
                                        <button
                                            onClick={() => { setNewMenuOpen(false); setShowCreate(true); }}
                                            data-testid="lh-c2-menu-new-collection"
                                        ><Plus size={13} /> New Collection</button>
                                        <button
                                            onClick={() => { setNewMenuOpen(false); setShowNewResource(true); }}
                                            data-testid="lh-c2-menu-new-resource"
                                        ><Plus size={13} /> New Resource</button>
                                    </div>
                                )}
                            </div>
                            <button className="lh-c2-btn" onClick={() => setShowBulk(true)} data-testid="lh-c2-open-bulk"><Upload size={14} /> Bulk Actions</button>
                            <button className="lh-c2-btn" onClick={() => setShowRecycle(true)} data-testid="lh-c2-open-recycle" title="Recycle bin"><Trash size={14} /> Recycle Bin</button>
                            <button
                                className="lh-c2-btn lh-c2-btn-handoffs"
                                onClick={openHandoffs}
                                data-testid="lh-c2-open-assignments"
                                title="Handoffs — collections and resources you've been assigned or have assigned to others"
                            >
                                <UserPlus size={14} /> Handoffs
                                {assignments.has_recent && <span className="lh-c2-badge-dot" data-testid="lh-c2-handoffs-dot" aria-label="New handoffs" />}
                            </button>
                            <div className="lh-c2-view-toggle">
                                <button className={viewMode === "grid" ? "active" : ""} onClick={() => setViewMode("grid")} data-testid="lh-c2-view-grid">
                                    <Grid3x3 size={13} /> Grid
                                </button>
                                <button className={viewMode === "mindmap" ? "active" : ""} onClick={() => setViewMode("mindmap")} data-testid="lh-c2-view-mindmap">
                                    <GitBranch size={13} /> Mind map
                                </button>
                            </div>
                        </div>
                    </div>

                    {viewMode === "grid" ? (
                        <div className="lh-c2-lib-layout">
                            <FilterChip
                                level={filterLevel} setLevel={setFilterLevel}
                                type={filterType} setType={setFilterType}
                                dept={filterDept} setDept={setFilterDept}
                                deptOptions={deptOptions}
                                matchCount={filtered.length}
                                totalCount={collections.length}
                                levelCounts={levelCounts}
                                typeCounts={typeCounts}
                                deptCounts={deptCounts}
                            />
                            <div className="lh-c2-depts">
                                {otherDepts.length === 0 ? (
                                    <div className="lh-c2-empty">
                                        <Compass size={40} />
                                        <p>No other departments match your filters.</p>
                                    </div>
                                ) : otherDepts.map((d) => (
                                    <DeptShelf
                                        key={d.id || "unassigned"}
                                        deptId={d.id}
                                        deptName={d.alias || d.name}
                                        collections={d.collections}
                                        maxCards={4}
                                        expanded={!!expandedDepts[d.id]}
                                        onToggle={toggleDeptExpanded}
                                        onOpen={setActiveCollection}
                                        onEdit={setEditingCollection}
                                        onDelete={deleteCollection}
                                        onAssign={openAssignForCollection}
                                        onReroll={rerollCover}
                                        canManage={canManageCollection}
                                        onReorder={searchQuery ? null : (from, to) => reorderCollectionsInShelf(
                                            (expandedDepts[d.id] ? d.collections : d.collections.slice(0, 4)), from, to,
                                        )}
                                        autoCovers={autoCovers}
                                        freshCovers={freshCovers}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <Concept2MindMap
                            collections={filtered}
                            depts={depts || []}
                            defaultDept={myDeptId}
                            myDeptId={myDeptId}
                            onOpenCollection={(c) => {
                                const full = collections.find((x) => x.id === c.id) || c;
                                setActiveCollection(full);
                            }}
                            onOpenResource={(r, c) => setActiveResource({ resource: r, collection: c })}
                            onReorderCollections={(shelfCollections, from, to) => reorderCollectionsInShelf(shelfCollections, from, to)}
                            canManageCollection={canManageCollection}
                        />
                    )}
                </section>

                {looseResources.length > 0 && viewMode === "grid" && (
                    <section className="lh-c2-wrap" style={{ marginTop: 40 }} data-testid="lh-c2-loose-shelf">
                        <div className="lh-c2-dept-row-head">
                            <div className="lh-c2-dept-row-head-left">
                                <h3>Loose Resources</h3>
                                <span className="count">{looseResources.length} standalone item{looseResources.length === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                        <div className="lh-c2-loose-grid">
                            {looseResources.map((r) => {
                                const pill = { article: "AR", video: "VI", doc: "DC", course: "CR", podcast: "PO", quiz: "QZ", book: "BK", note: "NT" }[(r.type || "note").toLowerCase()] || "FL";
                                const colour = { article: "#E7A33E", video: "#2EB4AC", doc: "#5B7FDE", course: "#C86B85", podcast: "#7FA65C", quiz: "#C6543F", book: "#38B26D", note: "#6B7280" }[(r.type || "note").toLowerCase()] || "#6B7280";
                                return (
                                    <button
                                        key={r.id}
                                        className="lh-c2-loose-card"
                                        onClick={() => setActiveResource({ resource: r, collection: null })}
                                        data-testid={`lh-c2-loose-${r.id}`}
                                    >
                                        <span className="lh-c2m-pill" style={{ background: colour }}>{pill}</span>
                                        <span className="lh-c2-loose-name">{r.name}</span>
                                        <span className="lh-c2-loose-meta">{r.type || "note"}{r.duration_minutes ? ` · ${r.duration_minutes}m` : ""}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}
            </div>

            <RerollCoverDialog collection={rerollFor} onClose={() => setRerollFor(null)} onGenerate={doReroll} />

            <AssignmentsPanel
                open={showAssignPanel}
                onClose={() => setShowAssignPanel(false)}
                tab={assignPanelTab}
                onTabChange={setAssignPanelTab}
                data={assignments}
                onOpenCollection={async (c) => {
                    /* Prefer the already-loaded row (keeps every field like
                     * cover_image / owner_department_name intact). Falls back
                     * to a synthetic stub — Concept2Modal will re-fetch its
                     * folders & resources by id anyway. */
                    const full = collections.find((x) => x.id === c.id) || c;
                    setActiveCollection(full);
                    setShowAssignPanel(false);
                }}
                onOpenResource={async (r) => {
                    let full = looseResources.find((x) => x.id === r.id);
                    if (!full) {
                        try {
                            const resp = await api.get(`/learning/resources/${r.id}`);
                            full = resp.data;
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "Couldn't open resource");
                            return;
                        }
                    }
                    const col = full.collection_id ? (collections.find((x) => x.id === full.collection_id) || null) : null;
                    setActiveResource({ resource: full, collection: col });
                    setShowAssignPanel(false);
                }}
            />

            {showWelcome && <WelcomeModal userName={user?.name} onClose={dismissWelcome} />}

            <HubAssistant
                open={assistantOpen}
                onOpen={() => setAssistantOpen(true)}
                onClose={() => setAssistantOpen(false)}
                user={user}
                onJumpToFilter={handleJumpToFilter}
            />

            {showCreate && (<Concept2CreateCollectionDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />)}
            {editingCollection && (
                <Concept2CreateCollectionDialog
                    initial={editingCollection}
                    onClose={() => setEditingCollection(null)}
                    onCreated={() => { setEditingCollection(null); load(); }}
                />
            )}
            {showRecycle && (
                <RecycleBinModal
                    canManage={canManageCollection}
                    onClose={() => setShowRecycle(false)}
                    onChanged={load}
                />
            )}
            {showBulk && (
                <Concept2BulkImportModal
                    onClose={() => setShowBulk(false)}
                    onImported={load}
                />
            )}
            {showNewResource && (
                <NewResourceDialog
                    onClose={() => setShowNewResource(false)}
                    onCreated={() => { setShowNewResource(false); load(); }}
                />
            )}
            {assigningItem && (
                <Concept2AssignDialog
                    kind={assigningItem.kind}
                    itemId={assigningItem.id}
                    itemName={assigningItem.name}
                    initial={assigningItem.assigned_to}
                    onClose={() => setAssigningItem(null)}
                    onSaved={async (ids) => {
                        // Reflect the fresh assignee count in the local list
                        // so the "Assigned to me" pill (if any) stays honest.
                        if (assigningItem.kind === "collection") {
                            setCollections((prev) => prev.map((c) => c.id === assigningItem.id ? { ...c, assigned_to: ids } : c));
                        }
                        setAssigningItem(null);
                        await load();
                    }}
                />
            )}

            {activeCollection && (
                <Concept2Modal
                    collection={activeCollection}
                    onClose={() => setActiveCollection(null)}
                    onOpenResource={(r, col) => setActiveResource({ resource: r, collection: col })}
                    onChanged={load}
                    autoCovers={autoCovers}
                    showCover={freshCovers.has(activeCollection.id)}
                    canManage={canManageCollection(activeCollection)}
                    onEditCollection={(c) => { setActiveCollection(null); setEditingCollection(c); }}
                    onDeleteCollection={async (c) => {
                        if (!window.confirm(`Move "${c.name}" to the Recycle Bin? You can restore within 30 days.`)) return;
                        try {
                            await api.delete(`/learning/collections/${c.id}`);
                            toast.success(`"${c.name}" moved to Recycle Bin`);
                            setActiveCollection(null);
                            load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                    }}
                    onRerollCover={rerollCover}
                />
            )}

            {activeResource && (
                <Concept2Viewer
                    resource={activeResource.resource}
                    collection={activeResource.collection}
                    onClose={() => setActiveResource(null)}
                    onStatusChange={() => { load(); }}
                    canManage={canManageResource(activeResource.resource, activeResource.collection)}
                    onResourceUpdated={(updated) => {
                        // Sync the updated resource into the viewer so the
                        // user sees their changes reflected immediately.
                        setActiveResource((prev) => prev ? { ...prev, resource: { ...prev.resource, ...updated } } : prev);
                        load();
                    }}
                    onEditResource={() => { /* handled inline; parent just refreshes */ load(); }}
                    onMoveResource={() => { load(); }}
                    onDeleteResource={async (r) => {
                        if (!window.confirm(`Move "${r.name}" to the Recycle Bin? You can restore within 30 days.`)) return;
                        try {
                            await api.delete(`/learning/resources/${r.id}`);
                            toast.success(`"${r.name}" moved to Recycle Bin`);
                            setActiveResource(null);
                            load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                    }}
                />
            )}
        </div>
    );
};

export default LearningHubConcept2;
